import { AppEvent, Result, SimpleObject, StatsObject } from '../types'
import { v4 as uuid } from 'uuid'
import apiFetch from '../util/apiFetch'
import calculateNewStatsObject from '../util/calculateNewStatsObject'
import cookieParser from 'js-cookie'
import {
	API_ENDPOINT,
	EVENTS,
	GAME_IDS,
	PID_URL,
	RESULTS_STORAGE_ID,
	STATS_STORAGE_ID,
} from '../config'

// Check to see if this player has a Waffle player ID that we can reuse.
if (PID_URL && !localStorage.getItem('pid')) {
	fetch(PID_URL, { credentials: 'include' })
		.then(response => response.json())
		.then(({ pid }) => {
			if (!localStorage.getItem('pid')) setPlayerId(pid)
		})
		.catch(error => console.log('error', error))
}

const GAME_ID = GAME_IDS.DAILY5
const INITIAL_STATS_OBJECT: StatsObject = {
	p: 0,
	sk: 0,
	b: 0,
	l: 0,
	s: 0,
	bt: 0,
	dst: [0, 0, 0, 0, 0],
}

// ================================================================================================
// Public functions

export function loadStatsFromStorage(): StatsObject {
	const statsString = localStorage.getItem(STATS_STORAGE_ID(GAME_ID))
	return statsString ? JSON.parse(statsString) : INITIAL_STATS_OBJECT
}

export function loadResultsFromStorage(): Result[] {
	const resultsString = localStorage.getItem(RESULTS_STORAGE_ID(GAME_ID))
	return resultsString ? JSON.parse(resultsString) : []
}

export default function saveStatsToStorage(stats: StatsObject) {
	const statsString = JSON.stringify(stats)
	localStorage.setItem(STATS_STORAGE_ID(GAME_ID), statsString)
	emit(EVENTS.STATS_CHANGED)
}

export function saveResultsToStorage(results: Result[]) {
	const resultsString = JSON.stringify(results)
	localStorage.setItem(RESULTS_STORAGE_ID(GAME_ID), resultsString)
}

export function setResult(newResult: Result): void {
	const results = loadResultsFromStorage()
	const existingResult = results.find(r => r.i === newResult.i)
	// ignore any new daily attempts after the first attempt
	if (existingResult) return

	results.push(newResult)
	saveResultsToStorage(results)

	const stats = loadStatsFromStorage()
	const newStats = calculateNewStatsObject(stats, newResult)
	saveStatsToStorage(newStats)

	// sync the new result to the server
	postDailyResultsToServer()
}

export function getStatsObject(): StatsObject {
	// Check if we have results that haven't been applied to the stats object yet for whatever reason
	let newStats = { ...loadStatsFromStorage() }
	const stats = loadStatsFromStorage()
	const results = loadResultsFromStorage()

	results
		.sort((a, b) => (a.i < b.i ? -1 : 1)) // Sort in ascending order
		.forEach(r => {
			const newResult: Result = r
			newStats = calculateNewStatsObject(newStats, newResult)
		})

	// If the stats seems to be different, with these results in consideration, then update it
	if (JSON.stringify(newStats) !== JSON.stringify(stats)) saveStatsToStorage(newStats)

	return newStats
}

export function getPlayed(): number {
	return getStatsObject().p
}

export function getStreak(): number {
	return getStatsObject().sk
}

export function getBestStreak(): number {
	return getStatsObject().b
}

export function getTotalStars(): number {
	const { dst, s } = getStatsObject()
	return s || dst.reduce((acc, val, i) => acc + val * (i + 1), 0)
}

export function getDistribution(): number[] {
	return getStatsObject().dst
}

export function setPlayerId(pid: string): void {
	localStorage.setItem('pid', pid)
}

export function getPlayerIdFromCookies() {
	const cookies = cookieParser.get()
	return cookies?.pid || null
}

export function getPlayerId(): string {
	let playerId = localStorage.getItem('pid')
	if (!playerId) {
		const cookies = cookieParser.get()
		playerId = cookies && cookies.pid
		localStorage.setItem('pid', playerId as string)
	}
	if (!playerId || playerId === 'undefined' || playerId === 'null') {
		playerId = uuid()
		localStorage.setItem('pid', playerId as string)
	}
	return playerId || ''
}

export function getShortPlayerId(): string {
	return getPlayerId().toUpperCase().substring(0, 8)
}

// =================================================================================================

const eventHandlers = {}

export function on(event: string, callback: (...args) => void): void {
	eventHandlers[event] = eventHandlers[event] || []
	eventHandlers[event].push(callback)
}

export function off(event: string, callback: (...args) => void): void {
	if (eventHandlers[event]) {
		eventHandlers[event] = eventHandlers[event].filter(handler => handler !== callback)
	}
}

function emit(event: AppEvent, ...args) {
	if (eventHandlers[event]) {
		eventHandlers[event].forEach(handler => handler(...args))
	}
}

// =================================================================================================

export async function postDailyResultsToServer(): Promise<void> {
	const results = loadResultsFromStorage()
	const stats = loadStatsFromStorage()
	const resultToSync = results.find(r => r.sync === false)
	if (!resultToSync) return

	try {
		const { i, sync: _, ...details } = resultToSync
		const apiResult = await postResult(getPlayerId(), i, details)
		if (apiResult && apiResult.p) {
			delete resultToSync.sync
			// After a few seconds, see if we've got any other results that should be posted to the server
			setTimeout(postDailyResultsToServer, 5000)
		}
		saveResultsToStorage(results)

		// If stats object received from server seems to be better than the one we have in local storage, use that one instead
		if (apiResult && apiResult.p >= stats.p) {
			saveStatsToStorage(apiResult)
		}
		// If we haven't got a recorded "best time" yet, but the server has one, then update our stats object
		if (apiResult && apiResult.bt && (!stats.bt || apiResult.bt < stats.bt)) {
			stats.bt = apiResult.bt
			saveStatsToStorage(stats)
		}
	} catch (e) {
		console.error(e)
	}
}

async function postResult(
	pid: string,
	i: number,
	details: SimpleObject,
	attempt = 0
): Promise<StatsObject> {
	const url = `${API_ENDPOINT}/results`
	const resultKey = [GAME_IDS.DAILY5, i, attempt].join('_')
	const resultToPost = { key: resultKey, ...details }
	const requestBody = { pid, result: resultToPost }
	const stats = (await apiFetch(url, 'POST', requestBody)) as StatsObject
	return stats
}
